import {
  For,
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";
import { isServer } from "solid-js/web";

import {
  AnimationMenuSlide,
  useDropdownMenu,
} from "@/components/Base/AnimationMenuSlide/index.tsx";
import { Button } from "@/components/Base/Button/Button";
import Popup from "@/components/Base/Popup/Popup";
import Icon from "@/components/Icon";
import { RepeatActivity } from "@/enum/activity";
import { useBonusCenter } from "@/hooks/useBonusCenter";
import { useRouter } from "@/hooks/useRouter";
import { t } from "@/i18n";
import { overFlowFlagManager } from "@/lib/OverFlowFlag";
import { addZero } from "@/lib/dateUtils";
import { useTimestampInSeconds } from "@/lib/hooks/useTimestamp";
import { isMobile } from "@/lib/isMobile";
import { formatToBindPoint } from "@/lib/pointUtils";
import { getBonusHeaderImg } from "@/lib/resFolder";
import { sdkManager } from "@/lib/sdk/sdkManager.ts";
import { getTimeRemaining } from "@/lib/utils";
import {
  bonusCenterStore,
  readyList,
  readyListCount,
  readyListType,
} from "@/stores/bonusCenterStore";
import {
  ActivityDetailStatus,
  ActivityTypeEnum,
} from "@/types/compontent/base/baseEnum";
import { SpecialActivityRewards } from "@/types/swaggerex/activity";

// 条件动画包装器组件
type ConditionalAnimationWrapperProps = {
  pcState: boolean;
  showAnimation: boolean;
  mobileState: boolean;
  onAnimationEnd?: () => void;
  onMobileClose?: () => void;
  popupRef?: { current: { handleClose: () => void } | null };
  children: unknown;
};

const ConditionalAnimationWrapper = (
  props: ConditionalAnimationWrapperProps,
): JSX.Element => {
  return (
    <div
      class="absolute right-0 top-full z-[100]"
      classList={{
        "w-[420px] mt-[15px]": !isMobile(),
      }}
    >
      <Show
        when={!isMobile() && (props.showAnimation || props.pcState)}
        fallback={
          <Popup
            show={props.mobileState}
            onClose={props.onMobileClose || (() => {})}
            direction="top"
            withHeader={false}
            withPadding={false}
            zIndex={49}
            offset={48}
            ref={(ref) => {
              if (props.popupRef) {
                props.popupRef.current = ref;
              }
            }}
          >
            {props.children as Element}
          </Popup>
        }
      >
        <AnimationMenuSlide
          state={props.pcState}
          onAnimationEnd={props.onAnimationEnd}
        >
          {props.children as Element}
        </AnimationMenuSlide>
      </Show>
    </div>
  );
};

export default function BonusDropdownMenu() {
  let dropdownRef: HTMLElement;
  const popupRef = { current: null as { handleClose: () => void } | null }; // 用于控制 Popup 的 ref
  const { push } = useRouter();
  const [isMobileOpen, setIsMobileOpen] = createSignal(false);
  const [isPcOpen, setIsPcOpen] = createSignal(false);
  // 使用自定义 Hook 管理下拉菜单状态
  const {
    isOpen,
    isAnimating,
    toggleDropdown,
    startCloseAnimation,
    handleAnimationEnd,
  } = useDropdownMenu();

  const list = createMemo(() => {
    return readyList();
  });

  const count = createMemo(() => {
    return readyListCount();
  });

  const type = createMemo(() => {
    return readyListType();
  });
  const flagDropdownPC = () => {
    setIsPcOpen(!isPcOpen());
  };

  const handleItemClick = () => {
    if (isPcOpen()) {
      handleMouseEnterOrLeave("leave");
    } else handleMouseEnterOrLeave("enter");
    flagDropdownPC();
  };

  const toggleDropdownNow = () => {
    if (isMobileOpen()) {
      // 如果当前是打开状态，调用 Popup 的 handleClose 方法来关闭
      if (popupRef.current?.handleClose) {
        popupRef.current.handleClose();
      } else {
        setIsMobileOpen(false);
      }
    } else {
      // 如果当前是关闭状态，直接打开
      setIsMobileOpen(true);
    }
  };
  // 点击外部关闭下拉菜单
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (dropdownRef && !dropdownRef.contains(target)) {
      if (isMobile() && isMobileOpen()) {
        if (popupRef.current?.handleClose) {
          popupRef.current.handleClose();
        }
      } else {
        handleMouseEnterOrLeave("leave");
        setIsPcOpen(false);
      }
    }
  };

  createEffect(
    on(
      createMemo(() => isMobile() && isMobileOpen()),
      (boolean) => {
        if (boolean) {
          overFlowFlagManager.addFlag();
        } else {
          overFlowFlagManager.removeFlag();
        }
      },
    ),
  );

  onMount(() => {
    if (!isServer) {
      document.addEventListener("click", handleClickOutside);
    }
  });

  onCleanup(() => {
    if (!isServer) {
      document.removeEventListener("click", handleClickOutside);
    }
  });

  const handleMouseEnterOrLeave = (type: "enter" | "leave") => {
    if (type === "enter") {
      toggleDropdown();
    } else {
      startCloseAnimation();
    }
  };

  return (
    <section
      class="relative"
      ref={(el) => (dropdownRef = el)}
      onclick={() => {
        sdkManager.tdReportEvent("activity_center_click", {
          from_page: window.location.pathname,
        });
        if (!count()) {
          push("bonus-center");
          return;
        }
        if (isMobile()) {
          toggleDropdownNow();
        } else {
          handleItemClick();
        }
      }}
      // onMouseLeave={() => handleMouseEnterOrLeave("leave")}
    >
      <div
        class="relative flex cursor-pointer items-center justify-center rounded-[8px]"
        classList={{
          "h-[40px] w-[50px]": !isMobile(),
          "h-[34px] w-[44px]": isMobile(),
        }}
        style={{
          background:
            "linear-gradient(180deg, rgba(254, 160, 19, 0.5) 0%, rgba(254, 160, 19, 0.15) 35%, rgba(254, 160, 19, 0.1) 50%, rgba(254, 160, 19, 0.15) 65%, rgba(254, 160, 19, 0.5) 100%)",
          "box-shadow":
            "inset 0px -1.17647px 0px rgba(237, 233, 134, 0.25), inset 0px 1.17647px 1.17647px rgba(255, 222, 48, 0.43)",
        }}
      >
        <Show when={count() > 0} fallback={<></>}>
          <div
            class="absolute flex h-[14px] items-center justify-center rounded-[4px] px-[4.5px] text-[10px] font-[700] leading-[12px]"
            classList={{
              "right-[-6px] top-0 px-[5.5px]": !isMobile(),
              "right-[-4px] top-[-2px]": isMobile(),
              "bg-up-tips text-general-butzi": type() === 1,
              " bg-general-highbut text-general-butzi": type() === 2,
            }}
          >
            {count()}
          </div>
        </Show>

        <img
          src={getBonusHeaderImg()}
          alt="bonus-header"
          loading="eager"
          class="h-[28px] w-[28px]"
        />
      </div>
      <ConditionalAnimationWrapper
        pcState={isOpen()}
        showAnimation={isAnimating()}
        mobileState={isMobileOpen()}
        onAnimationEnd={handleAnimationEnd}
        onMobileClose={() => setIsMobileOpen(false)}
        popupRef={popupRef}
      >
        <div
          class="relative max-h-[536px] overflow-hidden pb-[20px]"
          classList={{
            "rounded-[6px] bg-general-bg4 min-w-[420px] px-[15px] pt-[15px]":
              !isMobile(),
            "rounded-b-[15px] bg-general-bg2 min-w-[351px] px-[12px] pt-[12px]":
              isMobile(),
          }}
        >
          <div
            class="text-general-light text-left text-[14px] font-[700] leading-[17px]"
            classList={{
              "mb-[10px]": !isMobile(),
              "mb-[12px]": isMobile(),
            }}
          >
            {t("bonus.ready")}
          </div>

          <div
            class="flex max-h-[452px] flex-col gap-[8px] overflow-y-scroll overscroll-y-contain pb-[60px]"
            classList={{
              "max-h-[452px]": !isMobile(),
              "max-h-[370px]": isMobile(),
            }}
          >
            <For each={list()}>
              {(item, index) => <BonusItem {...item} index={index()} />}
            </For>
          </div>

          {/* 按钮 */}
          <div
            class="to-general-bg4 absolute bottom-0 left-0 right-0 h-[100px] bg-gradient-to-b from-transparent px-[15px]"
            classList={{
              "to-general-bg4": !isMobile(),
              "to-general-bg2": isMobile(),
            }}
            style={{
              background: `linear-gradient(to bottom, transparent 0%, var(--general-bg4) 35%`,
            }}
          >
            <div
              class="bg-general-bg7 mt-[36px] flex h-[44px] cursor-pointer items-center justify-center gap-[8px] rounded-[6px]"
              onClick={() => {
                sdkManager.tdReportEvent("activity_center_more_click", {
                  from_page: window.location.pathname,
                });
                push("bonus-center");
                startCloseAnimation();
              }}
            >
              <span
                classList={{
                  "text-[16px] font-extrabold": !isMobile(),
                  "text-[14px] font-extrabold leading-[17px]": isMobile(),
                }}
                style={{
                  background:
                    "linear-gradient(to right, var(--general-main), var(--general-assist))",
                  "-webkit-background-clip": "text",
                  "-webkit-text-fill-color": "transparent",
                }}
              >
                {t("bonus.dashboard")}
              </span>
              <Icon
                name="double-chevron"
                class={`text-general-main ${isMobile() ? "h-[12px] w-[12px]" : "h-[14px] w-[14px]"}`}
              />
            </div>
          </div>
        </div>
      </ConditionalAnimationWrapper>
    </section>
  );
}

type BonusItemProps = {
  type: number;
  atype: number;
  img: string;
  order: number;
  title: string;
  button: string;
  status: ActivityDetailStatus;
  receive_point: number;
  id: number;
  tips?: string;
  countdown?: number;
  reward?: SpecialActivityRewards;
  is_special?: boolean;
  index: number;
};

const BonusItem = (props: BonusItemProps) => {
  const { handleBonusClick, clickSpecialButton } = useBonusCenter();
  const details = createMemo(() => {
    return bonusCenterStore.bonusCenterCardData[props.atype];
  });
  const timeStamp = useTimestampInSeconds();
  const endTotal = createMemo(() => {
    if (props.countdown != null) {
      const total = getTimeRemaining(
        0,
        Math.max(props.countdown - timeStamp(), 0),
      );
      return {
        day: total.day,
        hour: addZero(total.hour),
        minute: addZero(total.minute),
        second: addZero(total.second),
      };
    }
    const endTime = details()?.details_status?.countdown ?? 0;
    const total = getTimeRemaining(0, Math.max(endTime - timeStamp(), 0));
    return {
      day: total.day,
      hour: addZero(total.hour),
      minute: addZero(total.minute),
      second: addZero(total.second),
    };
  });
  const endStr = createMemo(() => {
    let str = "";
    if (endTotal().day) {
      str += `<span class="text-general-light">${endTotal().day}</span><span class="mr-[4px] ">D</span>`;
    }
    str += `<span class="shrink-0 text-general-light">${endTotal().hour}:${endTotal().minute}:${endTotal().second}</span>`;
    return `<span class="text-general-light">${str}</span>`;
  });
  return (
    <div class="bg-general-bg3 flex items-center justify-center gap-[10px] rounded-[8px] py-[6px] pl-[8px] pr-[10px]">
      <img
        src={props.img}
        alt="bonus icon"
        loading="eager"
        classList={{
          "h-[72px] w-[72px]": !isMobile(),
          "h-[56px] w-[56px]": isMobile(),
        }}
      />

      <div class="flex flex-1 flex-col gap-[2px]">
        <span
          class="text-general-light font-[700]"
          classList={{
            "text-[16px] leading-[19px]": !isMobile(),
            "text-[14px] leading-[17px]": isMobile(),
          }}
        >
          {props.title}
        </span>
        <Show when={props.type === 2} fallback={<></>}>
          <span
            class="font-[600]"
            classList={{
              "text-[14px] leading-[17px]": !isMobile(),
              "text-[12px] leading-[15px]": isMobile(),
            }}
            style={
              details()?.status === ActivityDetailStatus.canReceive ||
              props.is_special
                ? {
                    background:
                      "linear-gradient(to right, var(--general-money-l), var(--general-money-r))",
                    "-webkit-background-clip": "text",
                    "-webkit-text-fill-color": "transparent",
                  }
                : {
                    background:
                      "linear-gradient(to right, var(--general-yellow-l), var(--general-yellow-r))",
                    "-webkit-background-clip": "text",
                    "-webkit-text-fill-color": "transparent",
                  }
            }
          >
            {formatToBindPoint({
              value: props.receive_point,
              isReward: true,
              isConverted: false,
            })}
          </span>
        </Show>
        <Show when={props.type === 3}>
          <span
            class="text-general-bright text-[10px] font-semibold"
            innerHTML={props.tips}
          ></span>
        </Show>
      </div>
      <Show
        when={
          details()?.status === ActivityDetailStatus.canReceive ||
          details()?.status === ActivityDetailStatus.canComplete ||
          props.is_special
        }
        fallback={<></>}
      >
        <Button
          variant={"primary"}
          class="h-[36px]"
          onClick={() => {
            sdkManager.tdReportEvent("activity_center_list_click", {
              from_page: window.location.pathname,
              activity_position_index: props.index,
              activity_id: props.atype,
            });
            if (props.is_special) {
              clickSpecialButton(props.reward);
            } else {
              handleBonusClick(props.atype, false, props.id.toString());
            }
          }}
          classList={{
            " min-w-[120px]": !isMobile(),
            "h-[36px] min-w-[102px]": isMobile(),
          }}
        >
          {props.button}
        </Button>
      </Show>
      {/* readyList自带状态toReceive也显示 */}
      <Show
        when={
          details()?.status === ActivityDetailStatus.toReceive ||
          props.type == 3
        }
        fallback={<></>}
      >
        <div
          class="bg-general-bg5 text-general-bright border-general-main flex h-[36px] flex-col items-center justify-center rounded-[6px] border-[1px] border-solid p-[5px]"
          onClick={() => handleBonusClick(props.atype)}
          classList={{
            " min-w-[120px]": !isMobile(),
            "h-[36px] min-w-[102px]": isMobile(),
          }}
        >
          <div class="text-general-darkzi text-[12px] font-extrabold">
            {`${t("item.ready_in")}`}
          </div>
          <div
            class="text-general-light text-[14px] font-extrabold"
            // 特殊处理：活动93（每日反水体验），即使展示倒计时依然可点击跳转，需显示手指头的指针样式
            classList={{
              "cursor-pointer":
                props.atype === ActivityTypeEnum.dailyCashbackExperience,
            }}
            innerHTML={endStr()}
          ></div>
        </div>
      </Show>
    </div>
  );
};
